// Epic D #2244 — surfaces the most recent materialised InterviewRoundSynthesis
// on the candidate report panel: round number, the round summary paragraph,
// coverage gaps with follow-up questions, and the LLM-drafted "areas to probe"
// list. Hidden entirely when no materialised synthesis exists — the absence is
// the right signal. Carries no recommendation: the synthesis never suggests an
// outcome (story #2325); the recruiter decides via stage transitions.

interface AreaToProbe {
  topic?: string | null;
  rationale?: string | null;
}

interface SynthesisGap {
  capability_dimension_id?: string | null;
  dimension_name?: string | null;
  note?: string | null;
  follow_up_questions?: string[] | null;
}

interface SynthesisContent {
  round_summary?: string | null;
  areas_to_probe?: AreaToProbe[] | null;
  gaps?: SynthesisGap[] | null;
}

export interface LatestSynthesisVersionData {
  id: string;
  roundNumber: number | null;
  generationState: string;
  content: SynthesisContent;
  effectiveFrom: string;
  author: { id: string; name: string };
}

interface LatestRoundOutcomeCardProps {
  synthesis: LatestSynthesisVersionData | null;
}

export function LatestRoundOutcomeCard({ synthesis }: LatestRoundOutcomeCardProps) {
  if (!synthesis) return null;

  const { roundNumber, content, effectiveFrom, author } = synthesis;
  const summary = content.round_summary ?? null;
  const gaps = (content.gaps ?? []).filter((g): g is SynthesisGap => g != null && g.note != null);
  const areas = (content.areas_to_probe ?? []).filter(
    (a): a is AreaToProbe => a != null && (a.topic != null || a.rationale != null),
  );

  return (
    <div data-testid="latest-round-outcome-card">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
        Latest round outcome{roundNumber != null ? ` — Round ${roundNumber}` : ""}
      </h4>
      <div className="space-y-3 rounded border border-border p-3">
        <div className="flex flex-wrap items-center gap-2 text-[10px] text-text-faint">
          <span>Recorded {new Date(effectiveFrom).toLocaleDateString()}</span>
          <span>&middot;</span>
          <span>{author.name}</span>
        </div>

        {summary && (
          <p
            className="whitespace-pre-line text-xs text-text-secondary"
            data-testid="round-summary"
          >
            {summary}
          </p>
        )}

        {gaps.length > 0 && (
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              Coverage gaps
            </p>
            <ul className="space-y-1" data-testid="coverage-gaps">
              {gaps.map((g, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: list is replaced wholesale on each synthesis version; no item identity to preserve
                <li key={`gap-${idx}`} className="text-xs text-text-secondary">
                  {g.dimension_name && (
                    <span className="font-medium text-text-primary">{g.dimension_name}</span>
                  )}
                  {g.dimension_name && g.note && <span> — </span>}
                  {g.note && <span>{g.note}</span>}
                  {(g.follow_up_questions ?? []).length > 0 && (
                    <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-[11px] text-text-muted">
                      {(g.follow_up_questions ?? []).map((q, qIdx) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: list is replaced wholesale on each synthesis version; no item identity to preserve
                        <li key={`gap-${idx}-q-${qIdx}`}>{q}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {areas.length > 0 && (
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              Areas to probe next round
            </p>
            <ul className="space-y-1" data-testid="areas-to-probe">
              {areas.map((a, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: list is replaced wholesale on each synthesis version; no item identity to preserve
                <li key={`area-${idx}`} className="text-xs text-text-secondary">
                  {a.topic && <span className="font-medium text-text-primary">{a.topic}</span>}
                  {a.topic && a.rationale && <span> — </span>}
                  {a.rationale && <span>{a.rationale}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
